import { ListingResource } from '../system/field';
import { TaskStatus } from './types';

/**
 * Service to track and manage resource operation tasks
 */
export class TaskManager {
  private static instance: TaskManager;
  private activeTasks: Map<string, TaskStatus>;
  private resourceTasks: Map<string, Set<string>>;
  private resourceIdTasks: Map<string, Set<string>>;
  private operationListeners: Map<string, Set<(status: TaskStatus) => void>>;
  private resourceListeners: Map<string, Set<(status: TaskStatus) => void>>;
  private resourceIdListeners: Map<string, Set<(status: TaskStatus) => void>>;
  private globalListeners: Set<(status: TaskStatus) => void>;

  private constructor() {
    this.activeTasks = new Map();
    this.resourceTasks = new Map();
    this.resourceIdTasks = new Map();
    this.operationListeners = new Map();
    this.resourceListeners = new Map();
    this.resourceIdListeners = new Map();
    this.globalListeners = new Set();
  }

  /**
   * Get the singleton instance of TaskManager
   */
  public static getInstance(): TaskManager {
    if (!TaskManager.instance) {
      TaskManager.instance = new TaskManager();
    }
    return TaskManager.instance;
  }

  /**
   * Start tracking a new resource operation task
   */
  public startTask(
    resourceType: ListingResource | string,
    operation: TaskStatus['operation'],
    details?: {
      resourceId?: string | number;
      taskName?: string;
      resource?: any;
    },
  ): string {
    const taskId = crypto.randomUUID();
    const resourceId = details?.resourceId?.toString();

    const taskStatus: TaskStatus = {
      inProgress: true,
      taskId,
      resourceType,
      operation,
      startTime: Date.now(),
      resourceId,
      taskName: details?.taskName,
      resource: details?.resource,
    };

    // Store task by ID
    this.activeTasks.set(taskId, taskStatus);

    // Index task by resource type
    if (!this.resourceTasks.has(resourceType.toString())) {
      this.resourceTasks.set(resourceType.toString(), new Set());
    }
    this.resourceTasks.get(resourceType.toString())!.add(taskId);

    // Index task by resource ID if available
    if (resourceId) {
      const resourceIdKey = `${resourceType}:${resourceId}`;
      if (!this.resourceIdTasks.has(resourceIdKey)) {
        this.resourceIdTasks.set(resourceIdKey, new Set());
      }
      this.resourceIdTasks.get(resourceIdKey)!.add(taskId);
    }

    // Notify all relevant listeners
    this.notifyListeners(taskStatus);

    return taskId;
  }

  /**
   * Finish a task by ID
   */
  public finishTask(taskId: string): void {
    const task = this.activeTasks.get(taskId);
    if (!task) return;

    // Mark as complete and add end time
    task.inProgress = false;
    task.endTime = Date.now();

    // Notify listeners of completion
    this.notifyListeners(task);

    // Remove task from active tracking
    this.activeTasks.delete(taskId);

    // Remove from resource type index
    const resourceTasks = this.resourceTasks.get(task.resourceType.toString());
    if (resourceTasks) {
      resourceTasks.delete(taskId);
      if (resourceTasks.size === 0) {
        this.resourceTasks.delete(task.resourceType.toString());
      }
    }

    // Remove from resource ID index
    if (task.resourceId) {
      const resourceIdKey = `${task.resourceType}:${task.resourceId}`;
      const resourceIdTasks = this.resourceIdTasks.get(resourceIdKey);
      if (resourceIdTasks) {
        resourceIdTasks.delete(taskId);
        if (resourceIdTasks.size === 0) {
          this.resourceIdTasks.delete(resourceIdKey);
        }
      }
    }
  }

  /**
   * Check if a specific task is in progress
   */
  public isTaskInProgress(taskId: string): boolean {
    const task = this.activeTasks.get(taskId);
    return task?.inProgress || false;
  }

  /**
   * Check if any task of a specific resource type is in progress
   */
  public isResourceBusy(resourceType: ListingResource | string): boolean {
    const tasks = this.resourceTasks.get(resourceType.toString());
    if (!tasks || tasks.size === 0) return false;

    for (const taskId of tasks) {
      const task = this.activeTasks.get(taskId);
      if (task?.inProgress) return true;
    }

    return false;
  }

  /**
   * Check if any task for a specific resource ID is in progress
   */
  public isResourceIdBusy(
    resourceType: ListingResource | string,
    resourceId: string | number,
  ): boolean {
    const resourceIdKey = `${resourceType}:${resourceId}`;
    const tasks = this.resourceIdTasks.get(resourceIdKey);
    if (!tasks || tasks.size === 0) return false;

    for (const taskId of tasks) {
      const task = this.activeTasks.get(taskId);
      if (task?.inProgress) return true;
    }

    return false;
  }

  /**
   * Check if any specific operation is in progress for a resource type
   */
  public isOperationInProgress(
    resourceType: ListingResource | string,
    operation: TaskStatus['operation'],
  ): boolean {
    const tasks = this.resourceTasks.get(resourceType.toString());
    if (!tasks || tasks.size === 0) return false;

    for (const taskId of tasks) {
      const task = this.activeTasks.get(taskId);
      if (task?.inProgress && task.operation === operation) return true;
    }

    return false;
  }

  /**
   * Check if any task is in progress for any resource type
   */
  public isAnyTaskInProgress(): boolean {
    for (const task of this.activeTasks.values()) {
      if (task.inProgress) return true;
    }
    return false;
  }

  /**
   * Get all active tasks for a resource type
   */
  public getActiveTasks(resourceType?: ListingResource | string): TaskStatus[] {
    if (!resourceType) {
      return Array.from(this.activeTasks.values()).filter(
        (task) => task.inProgress,
      );
    }

    const tasks: TaskStatus[] = [];
    const taskIds = this.resourceTasks.get(resourceType.toString());

    if (taskIds) {
      for (const taskId of taskIds) {
        const task = this.activeTasks.get(taskId);
        if (task?.inProgress) {
          tasks.push(task);
        }
      }
    }

    return tasks;
  }

  /**
   * Register a listener for all task status changes
   */
  public subscribeToAll(callback: (status: TaskStatus) => void): () => void {
    this.globalListeners.add(callback);

    return () => {
      this.globalListeners.delete(callback);
    };
  }

  /**
   * Register a listener for task status changes of a specific resource type
   */
  public subscribeToResource(
    resourceType: ListingResource | string,
    callback: (status: TaskStatus) => void,
  ): () => void {
    const key = resourceType.toString();

    if (!this.resourceListeners.has(key)) {
      this.resourceListeners.set(key, new Set());
    }

    this.resourceListeners.get(key)!.add(callback);

    return () => {
      const listeners = this.resourceListeners.get(key);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.resourceListeners.delete(key);
        }
      }
    };
  }

  /**
   * Register a listener for task status changes for a specific resource ID
   */
  public subscribeToResourceId(
    resourceType: ListingResource | string,
    resourceId: string | number,
    callback: (status: TaskStatus) => void,
  ): () => void {
    const key = `${resourceType}:${resourceId}`;

    if (!this.resourceIdListeners.has(key)) {
      this.resourceIdListeners.set(key, new Set());
    }

    this.resourceIdListeners.get(key)!.add(callback);

    return () => {
      const listeners = this.resourceIdListeners.get(key);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.resourceIdListeners.delete(key);
        }
      }
    };
  }

  /**
   * Register a listener for a specific operation type
   */
  public subscribeToOperation(
    operation: TaskStatus['operation'],
    callback: (status: TaskStatus) => void,
  ): () => void {
    if (!this.operationListeners.has(operation)) {
      this.operationListeners.set(operation, new Set());
    }

    this.operationListeners.get(operation)!.add(callback);

    return () => {
      const listeners = this.operationListeners.get(operation);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.operationListeners.delete(operation);
        }
      }
    };
  }

  /**
   * Notify all registered listeners for a task status change
   */
  private notifyListeners(status: TaskStatus): void {
    // Notify global listeners
    for (const listener of this.globalListeners) {
      try {
        listener(status);
      } catch (error) {
        console.error('Error in global task listener:', error);
      }
    }

    // Notify resource type listeners
    const resourceListeners = this.resourceListeners.get(
      status.resourceType.toString(),
    );
    if (resourceListeners) {
      for (const listener of resourceListeners) {
        try {
          listener(status);
        } catch (error) {
          console.error('Error in resource task listener:', error);
        }
      }
    }

    // Notify resource ID listeners
    if (status.resourceId) {
      const resourceIdKey = `${status.resourceType}:${status.resourceId}`;
      const resourceIdListeners = this.resourceIdListeners.get(resourceIdKey);

      if (resourceIdListeners) {
        for (const listener of resourceIdListeners) {
          try {
            listener(status);
          } catch (error) {
            console.error('Error in resource ID task listener:', error);
          }
        }
      }
    }

    // Notify operation listeners
    const operationListeners = this.operationListeners.get(status.operation);
    if (operationListeners) {
      for (const listener of operationListeners) {
        try {
          listener(status);
        } catch (error) {
          console.error('Error in operation task listener:', error);
        }
      }
    }
  }

  /**
   * Create a task wrapper for async operations
   */
  public async trackTask<T>(
    resourceType: ListingResource | string,
    operation: TaskStatus['operation'],
    details: {
      resourceId?: string | number;
      taskName?: string;
      resource?: any;
    } | null,
    taskFn: (taskId: string) => Promise<T>,
  ): Promise<T> {
    const taskId = this.startTask(
      resourceType,
      operation,
      details || undefined,
    );

    try {
      const result = await taskFn(taskId);
      return result;
    } finally {
      this.finishTask(taskId);
    }
  }
}

/**
 * Hook for accessing the task manager
 */
export function useTaskManager(): TaskManager {
  return TaskManager.getInstance();
}
